import { ChevronDownIcon } from '@langchain/untitled-ui-icons';
import { CircularProgress, LinearProgress, Option, Select } from '@mui/joy';

import cronstrue from 'cronstrue';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGraphAssistants } from '@/Pages/Graph/src/api/assistants';
import {
  useGraphAssistantsDebugSchema,
  useGraphThreads,
} from '@/Pages/Graph/src/api/useGraphSwr';
import { SchemaEditor } from '@/Pages/Graph/src/components/debug/common/schema-editor';
import { defaultSchemaRenderer } from '@/Pages/Graph/src/components/debug/common/schema-renderer';
import { pick } from '@/Pages/Graph/src/utils';
import { SpecificDateTimeInput } from '@/components/DateTimeRangePicker/SpecificDateTime';
import { Pane } from '@/components/Pane';
import { LoadMoreButton } from '@/components/RunsTable/LoadMoreButton';
import { Button, Input, Text, Tooltip } from '@/design-system';

import { useCreateCron, useCreateCronForThread } from '../../hooks/useCrons';

export const CreateCronPane = ({
  isOpen,
  setShowCreateCron,
}: {
  isOpen: boolean;
  setShowCreateCron: (isOpen: boolean) => void;
}) => {
  const [selectedThreadId, setSelectedThreadId] = useState<string>();
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>();
  const [schedule, setSchedule] = useState<string>('');
  const [input, setInput] = useState<Record<string, any>>({});
  const [endTime, setEndTime] = useState<string>('');
  const endDate = dayjs(endTime);

  const onClose = () => {
    setShowCreateCron(false);
    setSelectedThreadId(undefined);
    setSelectedAssistantId(undefined);
    setSchedule('');
    setInput({});
    setEndTime('');
  };

  const {
    assistants,
    hasMore,
    setSize,
    isLoading: isLoadingAssistants,
    isValidating: isValidatingAssistants,
  } = useGraphAssistants({});

  // select the first assistant when loaded
  useEffect(() => {
    if (!selectedAssistantId && assistants?.length) {
      setSelectedAssistantId(assistants[0].assistant_id);
    }
  }, [assistants, selectedAssistantId]);

  const {
    threads,
    hasMore: hasMoreThreads,
    setSize: setSizeThreads,
    isLoading: isLoadingThreads,
    isValidating: isValidatingThreads,
  } = useGraphThreads({});

  const { data: schemas, isLoading: isLoadingSchemas } =
    useGraphAssistantsDebugSchema(
      selectedAssistantId ? [selectedAssistantId] : undefined
    );
  const {
    input: inputSchema,
    state: stateSchema,
    inputRoot: inputSchemaRoot,
    stateRoot: stateSchemaRoot,
  } = schemas?.[0] ?? {};
  const onChange = useCallback(
    (inp, touched: string[] | undefined) => setInput(pick(inp, touched)),
    [setInput]
  );
  const isCronScheduleValid = useMemo(() => {
    if (!schedule) return true;
    try {
      cronstrue.toString(schedule, {
        throwExceptionOnParseError: true,
      });
      return true;
    } catch (e) {
      return false;
    }
  }, [schedule]);
  const isEndTimeValid = useMemo(() => {
    if (!endDate.isValid() || endDate.isBefore(dayjs())) return false;
    return true;
  }, [endDate]);
  const submitDisabledHint = useMemo(() => {
    if (!selectedAssistantId) return 'Select an assistant';
    if (!schedule) return 'Enter a cron schedule';
    if (!isCronScheduleValid) return 'Invalid cron schedule';
    if (!Object.keys(input).length) return 'Enter input';
    if (!isEndTimeValid && endTime) return 'Invalid end time';
    return undefined;
  }, [
    selectedAssistantId,
    schedule,
    isCronScheduleValid,
    input,
    isEndTimeValid,
    endTime,
  ]);

  const { trigger: createCron, isMutating: isCreatingCron } = useCreateCron();
  const { trigger: createCronForThread, isMutating: isCreatingCronForThread } =
    useCreateCronForThread({
      threadId: selectedThreadId ?? '',
    });
  const handleSubmit = async () => {
    if (submitDisabledHint) return;

    if (selectedThreadId) {
      await createCronForThread({
        thread_id: selectedThreadId,
        schedule,
        input,
        assistant_id: selectedAssistantId!,
        ...(endTime && { end_time: endTime }),
      });
    } else {
      await createCron({
        schedule,
        input,
        assistant_id: selectedAssistantId!,
        ...(endTime && { end_time: endTime }),
      });
    }
    onClose();
  };

  return (
    <Pane
      title="Create Cron"
      open={isOpen}
      onClose={onClose}
      topBarRightElement={
        <div className="flex items-center gap-2 px-4">
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            size="md"
          >
            Cancel
          </Button>

          {isCreatingCron || isCreatingCronForThread ? (
            <CircularProgress size="sm" />
          ) : (
            <Tooltip title={submitDisabledHint} side="bottom">
              <div>
                <Button
                  onClick={handleSubmit}
                  size="md"
                  disabled={!!submitDisabledHint}
                >
                  Save
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-12">
        <div className="flex w-full items-start gap-8">
          <div className="flex w-[200px] flex-col gap-1">
            <Text variant="h3">Assistant</Text>
            <Text variant="sm">Select an assistant to run the cron job.</Text>
          </div>
          <Select
            value={selectedAssistantId}
            onChange={(_, value) => setSelectedAssistantId(value ?? undefined)}
            indicator={<ChevronDownIcon className="h-4 w-4" />}
            placeholder="Select an assistant..."
            renderValue={(value) => {
              const assistant = assistants?.find(
                (a) => a.assistant_id === value?.value
              );
              return assistant?.name ?? assistant?.graph_id;
            }}
            sx={{
              flexGrow: 1,
            }}
          >
            {assistants?.map((assistant) => (
              <Option
                key={assistant.assistant_id}
                value={assistant.assistant_id}
              >
                <div className="flex flex-col gap-1">
                  {assistant.name ?? assistant.graph_id}
                </div>
              </Option>
            ))}
            {hasMore && (
              <LoadMoreButton
                isInitialLoad
                isLoading={isLoadingAssistants || isValidatingAssistants}
                isVirtual={false}
                onClick={() => {
                  setSize((size) => size + 1);
                }}
              />
            )}
          </Select>
        </div>

        <div className="flex w-full items-start gap-8">
          <div className="flex w-[200px] flex-col gap-1">
            <Text variant="h3">Thread (optional)</Text>
            <Text variant="sm">
              Select a thread to run the cron job on. If not selected, a
              stateless run will be created.
            </Text>
          </div>
          <Select
            value={selectedThreadId}
            onChange={(_, value) => setSelectedThreadId(value ?? undefined)}
            indicator={<ChevronDownIcon className="h-4 w-4" />}
            placeholder="Select a thread..."
            sx={{
              flexGrow: 1,
            }}
          >
            {selectedThreadId && <Option value={undefined}>None</Option>}
            {threads?.map((thread) => (
              <Option key={thread.thread_id} value={thread.thread_id}>
                {thread.thread_id}
              </Option>
            ))}
            {hasMoreThreads && (
              <LoadMoreButton
                isInitialLoad
                isLoading={isLoadingThreads || isValidatingThreads}
                isVirtual={false}
                onClick={() => {
                  setSizeThreads((size) => size + 1);
                }}
              />
            )}
          </Select>
        </div>
        <div className="flex w-full items-start gap-8">
          <div className="flex w-[200px] flex-col gap-1">
            <Text variant="h3">Schedule</Text>
            <Text variant="sm">Enter a schedule in UNIX cron format.</Text>
          </div>
          <Input
            placeholder="0 0 * * *"
            value={schedule}
            onChange={(value) => setSchedule(value)}
            isError={!isCronScheduleValid}
            hintText={
              !schedule
                ? undefined
                : !isCronScheduleValid
                ? 'Invalid cron schedule'
                : cronstrue.toString(schedule, {
                    throwExceptionOnParseError: false,
                  })
            }
          />
        </div>
        <div className="flex w-full items-start gap-8">
          <div className="flex w-[200px] flex-col gap-1">
            <Text variant="h3">End time (optional)</Text>
            <Text variant="sm">Enter a time to end the cron job.</Text>
          </div>
          <div className="flex flex-col gap-1">
            <SpecificDateTimeInput
              defaultValue={endTime}
              onChange={(value) => setEndTime(value)}
              direction="horizontal"
            />
            {!isEndTimeValid && endTime && (
              <Text variant="sm" className="text-error">
                Invalid end time
              </Text>
            )}
          </div>
        </div>
        <div className="flex w-full items-start gap-8">
          <div className="flex w-[200px] flex-col gap-1">
            <Text variant="h3">Input</Text>
            <Text variant="sm">Enter the input for the cron job.</Text>
          </div>
          {selectedAssistantId ? (
            isLoadingSchemas ? (
              <LinearProgress />
            ) : (
              <div className="flex-1">
                <SchemaEditor
                  input={input}
                  onChange={onChange}
                  schema={inputSchema ?? stateSchema}
                  rootSchema={inputSchemaRoot ?? stateSchemaRoot}
                  renderer={defaultSchemaRenderer}
                />
              </div>
            )
          ) : (
            <Text variant="sm" className="mx-auto">
              Select an assistant to enter input.
            </Text>
          )}
        </div>
      </div>
    </Pane>
  );
};
